import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { User } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@virtual-care/shadcn';
import { SearchInput, TableRowSort, client } from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { AccountStatusBadge } from './account-status-badge.tsx';
import { EmailVerifiedBadge } from './email-verified-badge.tsx';
import {
  type HomeCareVendor,
  VendorActionsCell,
  getVendorDisplayName,
} from './vendor-actions-cell.tsx';

export default function HomeCareIndividualsPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [accountStatusFilter, setAccountStatusFilter] = useState<
    string | undefined
  >(undefined);

  const columns: ColumnDef<HomeCareVendor>[] = useMemo(() => {
    return [
      {
        id: 'name',
        accessorFn: (row) => getVendorDisplayName(row),
        header: 'Name',
        cell: ({ row }) => <div>{getVendorDisplayName(row.original)}</div>,
      },
      {
        accessorKey: 'accountStatus',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const vendor = row.original;
          return (
            <AccountStatusBadge
              status={vendor.accountStatus}
              suspensionReason={vendor.suspensionReason}
            />
          );
        },
      },
      {
        id: 'emailVerified',
        header: 'Email',
        enableSorting: false,
        cell: ({ row }) => {
          const verified =
            (row.original as HomeCareVendor & { emailVerified?: boolean })
              .emailVerified ?? false;
          return <EmailVerifiedBadge verified={verified} />;
        },
      },
      {
        accessorKey: 'email',
        header: 'Email Address',
        cell: ({ row }) => (
          <div className="lowercase">{row.getValue('email')}</div>
        ),
      },
      {
        accessorKey: 'phoneNumber',
        enableSorting: false,
        header: 'Phone Number',
        cell: ({ row }) => <div>{row.getValue('phoneNumber') || 'N/A'}</div>,
      },
      {
        id: 'location',
        header: 'City',
        enableSorting: false,
        cell: ({ row }) => {
          const vendor = row.original;
          if (vendor.vendorType === 'INDIVIDUAL' && vendor.individualProfile) {
            return <div>{vendor.individualProfile.cityName}</div>;
          }
          return <div>-</div>;
        },
      },
      {
        id: 'services',
        header: 'Services',
        enableSorting: false,
        cell: ({ row }) => {
          const services = row.original.vendorServices || [];
          return (
            <Badge variant="outline">
              {services.length} service{services.length !== 1 ? 's' : ''}
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        cell: ({ row }) => <VendorActionsCell vendor={row.original} />,
      },
    ];
  }, []);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useQuery({
    queryKey: [
      'homecare-vendors',
      pagination,
      sorting,
      searchQuery,
      'INDIVIDUAL',
      accountStatusFilter,
    ],
    queryFn: () => {
      const sortId = sorting.length > 0 ? sorting[0].id : undefined;
      const validSortBy =
        sortId === 'email' || sortId === 'createdAt' ? sortId : undefined;
      return client.request('GET /homecare', {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        query: searchQuery || undefined,
        sortBy: validSortBy,
        sortOrder:
          sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
        vendorType: 'INDIVIDUAL',
        accountStatus: accountStatusFilter as
          | 'REGISTERED'
          | 'ACTIVE'
          | 'SUSPENDED'
          | 'REJECTED'
          | undefined,
      });
    },
    placeholderData: keepPreviousData,
  });

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="w-full">
      <CountablePageHeader
        title="HomeCare Individuals"
        totalCount={`${dataQuery.data?.totalCount || 0} total`}
        icon={User}
      />

      <div className="mb-4 flex flex-1 gap-2">
        <SearchInput
          className="flex-1"
          placeholder="Search vendors..."
          value={searchQuery}
          onChange={(value) => setSearchQuery(value)}
          onClear={() => setSearchQuery('')}
        />
        <Select
          value={accountStatusFilter || 'all'}
          onValueChange={(value) =>
            setAccountStatusFilter(value === 'all' ? undefined : value)
          }
        >
          <SelectTrigger className="min-h-11">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="REGISTERED">Registered</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="SUSPENDED">Suspended</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
}
